import type { LatestAttachmentContext } from './types';
import { webchatApi } from '../services/api';

export function normalizeMatchText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[_./-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function buildCandidateKeys(fieldName: string, label: string, prop: Record<string, unknown> | undefined): string[] {
  const hints = [
    fieldName,
    label,
    typeof prop?.title === 'string' ? prop.title : '',
    typeof prop?.description === 'string' ? prop.description : '',
    typeof prop?.title_he === 'string' ? prop.title_he : '',
    typeof prop?.description_he === 'string' ? prop.description_he : '',
  ];

  const candidates = new Set<string>();
  for (const hint of hints) {
    const normalized = normalizeMatchText(hint);
    if (!normalized) continue;
    candidates.add(normalized);
    candidates.add(normalized.replace(/\s+/g, ''));

    const tokens = normalized.split(' ').filter(token => token.length >= 3);
    for (let i = 0; i < tokens.length; i++) {
      candidates.add(tokens[i]);
      if (i < tokens.length - 1) {
        candidates.add(`${tokens[i]} ${tokens[i + 1]}`);
      }
    }
  }

  return [...candidates];
}

function toNormalizedTokenSet(values: string[]): Set<string> {
  const tokens = new Set<string>();

  for (const value of values) {
    const normalized = normalizeMatchText(value);
    if (!normalized) continue;

    tokens.add(normalized);
    tokens.add(normalized.replace(/\s+/g, ''));

    const parts = normalized.split(' ').filter(Boolean);
    for (const part of parts) {
      tokens.add(part);
    }
  }

  return tokens;
}

function getFileExtension(filename?: string): string | undefined {
  if (!filename) return undefined;
  const idx = filename.lastIndexOf('.');
  if (idx === -1 || idx === filename.length - 1) return undefined;
  return filename.slice(idx + 1).toLowerCase();
}

type AttachmentInferenceRule = {
  value: (ctx: { attachment: LatestAttachmentContext; fileExtension?: string }) => string | undefined;
  aliases: string[];
  when?: (ctx: {
    attachment: LatestAttachmentContext;
    fileExtension?: string;
    propType: string;
  }) => boolean;
};

const ATTACHMENT_INFERENCE_RULES: AttachmentInferenceRule[] = [
  {
    value: ({ attachment }) => attachment.storagePath,
    aliases: ['s3', 'storage path', 'storagepath', 's3 key', 'file key', 'object key', 'filename from s3'],
  },
  {
    value: ({ attachment }) => attachment.storagePath ? JSON.stringify([attachment.storagePath]) : undefined,
    aliases: ['documents', 'attachments', 'files', 'uploaded files', 'uploaded documents', 'מסמכים', 'קבצים'],
    when: ({ attachment, propType }) => Boolean(attachment.storagePath) && propType === 'array',
  },
  {
    value: ({ attachment }) => attachment.mimeType,
    aliases: ['mime', 'mime type', 'content type', 'media type', 'type of file'],
  },
  {
    value: ({ attachment }) => attachment.originalName,
    aliases: ['original name', 'original filename', 'source filename', 'שם מקורי'],
  },
  {
    value: ({ fileExtension }) => fileExtension,
    aliases: ['extension', 'file extension', 'suffix', 'סיומת'],
  },
  {
    value: ({ attachment }) => (
      typeof attachment.sizeBytes === 'number' ? String(attachment.sizeBytes) : undefined
    ),
    aliases: ['size', 'bytes', 'file size', 'גודל'],
  },
  {
    value: ({ attachment }) => attachment.fileId,
    aliases: ['file id', 'fileid', 'attachment id', 'מזהה קובץ'],
  },
  {
    value: ({ attachment }) => attachment.fileHandle,
    aliases: ['file handle', 'filehandle', 'ידית קובץ'],
  },
  {
    value: ({ attachment }) => attachment.filename,
    aliases: ['filename', 'file name', 'document name', 'שם הקובץ'],
  },
];

function getAttachmentRuleScore(
  rule: AttachmentInferenceRule,
  candidateTokens: Set<string>,
  haystack: string,
): number {
  let score = 0;

  for (const alias of rule.aliases) {
    const normalizedAlias = normalizeMatchText(alias);
    if (!normalizedAlias) continue;

    const aliasTokens = normalizedAlias.split(' ').filter(Boolean);
    const collapsedAlias = normalizedAlias.replace(/\s+/g, '');
    const hasExactAlias =
      candidateTokens.has(normalizedAlias) ||
      candidateTokens.has(collapsedAlias) ||
      haystack.includes(normalizedAlias);

    if (hasExactAlias) {
      score = Math.max(score, 100 + aliasTokens.length);
      continue;
    }

    const overlap = aliasTokens.filter(token => candidateTokens.has(token)).length;
    if (overlap > 0) {
      score = Math.max(score, overlap * 10);
    }
  }

  return score;
}

/**
 * Hint-match a field name/label/prop against an attachment's fields to decide
 * which concrete value (storagePath, filename, fileId, etc.) the field wants.
 * Mirrors mcp-server/src/webchat/services/chat-orchestrator.service.ts inferValueForField —
 * keep in sync if either changes.
 * Returns undefined if no rule scores highly enough to be confident.
 */
export function matchAttachmentRule(
  field: { name: string; label: string },
  prop: Record<string, unknown> | undefined,
  attachment: LatestAttachmentContext,
): string | undefined {
  const candidates = buildCandidateKeys(field.name, field.label, prop);
  const haystack = normalizeMatchText(candidates.join(' '));
  const candidateTokens = toNormalizedTokenSet(candidates);
  const propType = typeof prop?.type === 'string' ? prop.type.toLowerCase() : '';
  const fileExtension = getFileExtension(attachment.filename || attachment.originalName);
  const ctx = { attachment, fileExtension, propType };

  let bestMatch: { score: number; value: string | undefined } | null = null;

  for (const rule of ATTACHMENT_INFERENCE_RULES) {
    if (rule.when && !rule.when(ctx)) continue;

    const value = rule.value(ctx);
    if (value == null) continue;

    const score = getAttachmentRuleScore(rule, candidateTokens, haystack);
    if (score < 20) continue;

    if (!bestMatch || score > bestMatch.score) {
      bestMatch = { score, value };
    }
  }

  return bestMatch?.value;
}

/** Parse a "[File: name (s3_key: ...; mime_type: ...)]" marker's inner text back into structured fields. */
export function parseFileReference(rawRef: string): LatestAttachmentContext {
  const parenIndex = rawRef.indexOf(' (');
  if (parenIndex === -1 || !rawRef.endsWith(')')) {
    return { filename: rawRef.trim() };
  }

  const filename = rawRef.slice(0, parenIndex).trim();
  const metaText = rawRef.slice(parenIndex + 2, -1);
  const meta = new Map<string, string>();

  for (const segment of metaText.split(';')) {
    const sep = segment.indexOf(':');
    if (sep === -1) continue;
    const key = segment.slice(0, sep).trim().toLowerCase();
    const value = segment.slice(sep + 1).trim();
    if (key && value) meta.set(key, value);
  }

  const sizeRaw = meta.get('size_bytes');
  const sizeBytes = sizeRaw ? Number(sizeRaw) : undefined;

  return {
    filename,
    storagePath: meta.get('s3_key'),
    mimeType: meta.get('mime_type'),
    originalName: meta.get('original_name'),
    sizeBytes: Number.isFinite(sizeBytes) ? sizeBytes : undefined,
    fileId: meta.get('file_id'),
    fileHandle: meta.get('file_handle'),
  };
}

/**
 * Render `[File: name (s3_key: ...; mime_type: ...)]` markers as just the filename, for
 * display only. The full marker (with metadata) must stay intact in the underlying message
 * content sent to the backend - it's what the LLM/tool call reads, and what
 * getLatestAttachmentContext later re-parses for prefill. This only prettifies what the
 * user sees in the bubble.
 */
export function stripFileReferenceMetadata(text: string): string {
  return text.replace(/\[File:\s*([^\]]+?)\]/g, (_match, inner: string) => {
    return parseFileReference(inner).filename || inner;
  });
}

export function buildFileReference(attachment: LatestAttachmentContext): string {
  const meta: string[] = [];
  if (attachment.storagePath) meta.push(`s3_key: ${attachment.storagePath}`);
  if (attachment.mimeType) meta.push(`mime_type: ${attachment.mimeType}`);
  if (attachment.originalName) meta.push(`original_name: ${attachment.originalName}`);
  if (typeof attachment.sizeBytes === 'number') meta.push(`size_bytes: ${attachment.sizeBytes}`);
  if (attachment.fileId) meta.push(`file_id: ${attachment.fileId}`);
  if (attachment.fileHandle) meta.push(`file_handle: ${attachment.fileHandle}`);

  return meta.length > 0
    ? `[File: ${attachment.filename} (${meta.join('; ')})]`
    : `[File: ${attachment.filename}]`;
}

/** Upload a file for a form field. Pass an AbortSignal so a stale selection can be cancelled. */
export async function uploadForFormField(
  file: File,
  signal?: AbortSignal,
): Promise<{ attachment: LatestAttachmentContext; reference: string }> {
  const result = await webchatApi.upload(file, signal);
  const attachment: LatestAttachmentContext = {
    // Empty string is exactly as "missing" as undefined for these string fields
    // (unlike size, where 0 is a meaningful value — that one uses ?? below).
    filename: result.filename || result.originalName || file.name,
    originalName: result.originalName || result.filename || file.name,
    mimeType: result.mimeType || file.type,
    sizeBytes: result.size ?? file.size,
    storagePath: result.storagePath,
    fileId: result.fileId,
    fileHandle: result.fileHandle,
  };
  return { attachment, reference: buildFileReference(attachment) };
}

/**
 * Decide the concrete value to store for a file-type form field right after upload,
 * mirroring the backend's own hint-matching (chat-orchestrator.service.ts inferValueForField)
 * so a field's value resolves the same way whether the tool call is LLM-mediated or a direct
 * confirmation-args submission (which has no server-side re-resolution pass).
 */
export function resolveFileFieldValue(
  field: { name: string; label: string },
  prop: Record<string, unknown> | undefined,
  attachment: LatestAttachmentContext,
): string {
  return matchAttachmentRule(field, prop, attachment) ?? buildFileReference(attachment);
}
